/**
 * Trigger a browser download for a presigned URL.
 *
 * The file is fetched as a blob and saved via an object URL rather than pointing an
 * `<a download>` at the presigned URL directly. A direct-href anchor click is treated by
 * the browser as a navigation, which fires any `beforeunload` guard on the page (the
 * correction flow installs one while a correction is open) — so the user got a spurious
 * "Leave page?" dialog on every download. A blob object URL never navigates, so no dialog.
 */
export async function triggerDownload(url: string, filename: string): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    a.click();
  } finally {
    // Revoke on the next tick so the click has started the download first.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
}

/** Last path segment of an S3 key, used as the download filename. */
export function filenameFromKey(key: string, fallback = "review.xlsx"): string {
  return key.split("/").pop() || fallback;
}

/** Human-readable file size in MB with two decimals. */
export function formatMb(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}
